import React, { useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { GuildReportsResponse, GuildReportSummary } from '../types';
import HelpTip from './HelpTip';

const RECENT_LOG_COUNT = 5;
// 0 = the most recent RECENT_LOG_COUNT logs, otherwise a month window.
const PERIOD_OPTIONS = [0, 1, 2, 3, 6, 12];
const WCL_REPORT_URL_PATTERN = /warcraftlogs\.com\/reports\/([A-Za-z0-9]+)/;
const ZONE_ICON_BASE_URL = 'https://wow.zamimg.com/images/wow/icons/medium';

// Keyed by backend tbcRaids key; a log gets one icon per raid it has boss pulls in.
const RAID_ICONS: Record<string, { icon: string; label: string }> = {
    karazhan: { icon: 'achievement_boss_princemalchezaar_02', label: 'Karazhan' },
    gruul: { icon: 'achievement_boss_gruulthedragonkiller', label: "Gruul's Lair" },
    magtheridon: { icon: 'achievement_boss_magtheridon', label: "Magtheridon's Lair" },
    ssc: { icon: 'achievement_boss_ladyvashj', label: 'Serpentshrine Cavern' },
    tk: { icon: "achievement_boss_kael'thassunstrider_01", label: 'Tempest Keep' },
    hyjal: { icon: 'achievement_boss_archimonde-', label: 'Hyjal Summit' },
    bt: { icon: 'achievement_boss_illidan', label: 'Black Temple' },
    za: { icon: 'achievement_boss_zuljin', label: "Zul'Aman" },
    swp: { icon: 'achievement_boss_kiljaedan', label: 'Sunwell Plateau' },
};

const reportRaidIcons = (report: GuildReportSummary): Array<{ key: string; url: string; label: string }> =>
    (report.raidKeys ?? []).flatMap((key) => {
        const raid = RAID_ICONS[key];
        return raid
            ? [{ key, url: `${ZONE_ICON_BASE_URL}/${encodeURIComponent(raid.icon)}.jpg`, label: raid.label }]
            : [];
    });

interface GuildLogPickerProps {
    onApplyUrls: (urls: string[]) => void;
    /** A pasted report URL skips the list and loads that log on its own. */
    onLoadSingleUrl: (url: string) => void;
    disabled?: boolean;
}

interface ZoneGroup {
    id: number;
    name: string;
    codes: string[];
}

const zoneGroups = (reports: GuildReportSummary[]): ZoneGroup[] => {
    const groups = new Map<number, ZoneGroup>();
    for (const report of reports) {
        const group = groups.get(report.zoneId);
        if (group) {
            group.codes.push(report.code);
        } else {
            groups.set(report.zoneId, { id: report.zoneId, name: report.zoneName, codes: [report.code] });
        }
    }
    return Array.from(groups.values()).sort((a, b) => b.codes.length - a.codes.length || a.name.localeCompare(b.name));
};

const formatReportDate = (timestamp: number): string =>
    new Date(timestamp).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const formatReportDuration = (startTime: number, endTime: number): string => {
    const totalMinutes = Math.max(0, Math.round((endTime - startTime) / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${minutes}m`;
};

const periodLabel = (monthsBack: number): string =>
    monthsBack === 0 ? `Recent (${RECENT_LOG_COUNT})` : `${monthsBack} month${monthsBack === 1 ? '' : 's'}`;

const apiError = (err: any, fallback: string): string =>
    err.response?.data?.error || err.response?.data?.details || fallback;

const GuildLogPicker: React.FC<GuildLogPickerProps> = ({ onApplyUrls, onLoadSingleUrl, disabled }) => {
    const urlInputId = useId();
    const [monthsBack, setMonthsBack] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [guildName, setGuildName] = useState('MANA UP');
    const [fetchedLabel, setFetchedLabel] = useState(periodLabel(0));
    const [reports, setReports] = useState<GuildReportSummary[]>([]);
    const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
    const [urlText, setUrlText] = useState('');

    const onApplyUrlsRef = useRef(onApplyUrls);
    onApplyUrlsRef.current = onApplyUrls;

    const zones = zoneGroups(reports);
    const selectedCount = reports.filter((report) => selectedCodes.has(report.code)).length;
    const busy = disabled || loading;

    useEffect(() => {
        onApplyUrlsRef.current(reports.filter((report) => selectedCodes.has(report.code)).map((report) => report.url));
    }, [reports, selectedCodes]);

    const loadGuildReports = async (period: number) => {
        setLoading(true);
        setError(null);

        try {
            const body = period === 0 ? { limit: RECENT_LOG_COUNT } : { monthsBack: period };
            const response = await axios.post<GuildReportsResponse>('/api/attendance/guild-reports', body);
            const data = response.data;
            const nextReports = Array.isArray(data.reports) ? data.reports : [];
            const name = data.guildName || 'MANA UP';

            setGuildName(name);
            setFetchedLabel(periodLabel(period));
            setReports(nextReports);
            setSelectedCodes(new Set());

            if (nextReports.length === 0) {
                setError(`No reports found for ${name}.`);
            }
        } catch (err: any) {
            setError(apiError(err, 'Failed to fetch guild logs.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGuildReports(0);
    }, []);

    const handleUrlChange = (value: string) => {
        if (WCL_REPORT_URL_PATTERN.test(value)) {
            setUrlText('');
            onLoadSingleUrl(value.trim());
            return;
        }
        setUrlText(value);
    };

    const handleToggleReport = (code: string) => {
        setSelectedCodes((prev) => {
            const next = new Set(prev);
            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }
            return next;
        });
    };

    const handleToggleZone = (zone: ZoneGroup) => {
        setSelectedCodes((prev) => {
            const next = new Set(prev);
            const allSelected = zone.codes.every((code) => next.has(code));
            for (const code of zone.codes) {
                if (allSelected) {
                    next.delete(code);
                } else {
                    next.add(code);
                }
            }
            return next;
        });
    };

    return (
        <div className="guild-log-picker">
            <div className="trinket-filter-row" style={{ marginTop: 0 }}>
                <h4 className="heading-with-help">
                    Logs
                    <HelpTip label="Logs">
                        <p>
                            Paste any Warcraft Logs report URL to load that log on its own, or pull {guildName} uploads
                            and tick the logs you want.
                        </p>
                    </HelpTip>
                </h4>
            </div>
            <label htmlFor={urlInputId} className="analyzer-description">
                Paste a Warcraft Logs URL to load just that log.
            </label>
            <input
                id={urlInputId}
                type="text"
                className="log-url-single-input"
                value={urlText}
                disabled={disabled}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://fresh.warcraftlogs.com/reports/ABC123"
                spellCheck={false}
                autoComplete="off"
            />

            <div className="guild-log-picker-controls">
                <label className="guild-log-months">
                    <span>Past</span>
                    <select value={monthsBack} disabled={busy} onChange={(e) => setMonthsBack(Number(e.target.value))}>
                        {PERIOD_OPTIONS.map((period) => (
                            <option key={period} value={period}>
                                {periodLabel(period)}
                            </option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    className="select-button"
                    onClick={() => loadGuildReports(monthsBack)}
                    disabled={busy}
                >
                    {loading ? 'Fetching guild logs...' : 'Fetch guild logs'}
                </button>
            </div>
            {error && (
                <div className="error-message" style={{ marginTop: '12px', marginBottom: 0 }}>
                    {error}
                </div>
            )}

            {reports.length > 0 && (
                <>
                    <div className="trinket-filter-row">
                        <h5 style={{ margin: 0 }}>
                            {guildName} · {fetchedLabel} — {selectedCount}/{reports.length} selected
                        </h5>
                        <div className="guild-log-zone-actions">
                            <button
                                type="button"
                                className="trinket-filter-clear"
                                onClick={() => setSelectedCodes(new Set(reports.map((report) => report.code)))}
                                disabled={busy || selectedCount === reports.length}
                            >
                                All
                            </button>
                            <button
                                type="button"
                                className="trinket-filter-clear"
                                onClick={() => setSelectedCodes(new Set())}
                                disabled={busy || selectedCount === 0}
                            >
                                None
                            </button>
                        </div>
                    </div>
                    {zones.length > 1 && (
                        <div className="guild-log-zone-chips">
                            {zones.map((zone) => {
                                const zoneSelected = zone.codes.filter((code) => selectedCodes.has(code)).length;
                                return (
                                    <button
                                        key={zone.id}
                                        type="button"
                                        className={`guild-log-zone-chip${zoneSelected === zone.codes.length ? ' is-active' : ''}`}
                                        onClick={() => handleToggleZone(zone)}
                                        disabled={busy}
                                    >
                                        {zone.name} {zoneSelected}/{zone.codes.length}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <ul className="log-slate-list">
                        {reports.map((report) => {
                            const checked = selectedCodes.has(report.code);
                            const raidIcons = reportRaidIcons(report);
                            return (
                                <li key={report.code} className="log-slate">
                                    <label className="log-slate-main">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={busy}
                                            onChange={() => handleToggleReport(report.code)}
                                        />
                                        <span className="log-slate-zone-icons">
                                            {raidIcons.length > 0 ? (
                                                raidIcons.map((raid) => (
                                                    <img
                                                        key={raid.key}
                                                        className="log-slate-zone-icon"
                                                        src={raid.url}
                                                        alt={raid.label}
                                                        title={raid.label}
                                                    />
                                                ))
                                            ) : (
                                                <span className="log-slate-zone-icon" aria-hidden="true" />
                                            )}
                                        </span>
                                        <span className="log-slate-body">
                                            <span className="log-slate-title">{report.title}</span>
                                            <span className="log-slate-meta">
                                                <span>{report.zoneName}</span>
                                                <span>{formatReportDate(report.startTime)}</span>
                                                <span>{formatReportDuration(report.startTime, report.endTime)}</span>
                                                {report.owner && <span>by {report.owner}</span>}
                                            </span>
                                        </span>
                                    </label>
                                    <div className="log-slate-actions">
                                        <a
                                            href={report.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="log-slate-code"
                                        >
                                            {report.code}
                                        </a>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </div>
    );
};

export default GuildLogPicker;
